import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { useBankMarket } from './hooks/useBankMarket'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import { ALL_SCOPE1_CLASSES, SCOPE1_CLASS_GROUPS } from '../../types/bruktbilfinansiering'

interface Props {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
}

type Mode = 'volum' | 'antall'

const COLORS = ['#1e3a8a', '#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe']
const fmtNum = (n: number) => new Intl.NumberFormat('nb-NO').format(n)
const fmtNok = (n: number) =>
  `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(n / 1_000_000)} mill.`
const fmtPct = (p: number) => `${(p * 100).toFixed(1)} %`

export default function BankTab({ bankOrgNr, bankName, filters }: Props) {
  const { t } = useTranslation('bbf')
  const [mode, setMode] = useState<Mode>('volum')
  const [selectedMerke] = useState<string[]>([])
  const [selectedClasses, setSelectedClasses] = useState<string[]>([...ALL_SCOPE1_CLASSES])

  const { data, loading, error } = useBankMarket(bankOrgNr, filters, selectedMerke, selectedClasses)

  function toggleClass(cls: string) {
    setSelectedClasses(prev =>
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
    )
  }

  function selectGroup(group: string) {
    const groupClasses = SCOPE1_CLASS_GROUPS[group] as string[]
    const allSelected = groupClasses.every(c => selectedClasses.includes(c))
    if (allSelected) {
      setSelectedClasses(prev => prev.filter(c => !groupClasses.includes(c)))
    } else {
      setSelectedClasses(prev => [...new Set([...prev, ...groupClasses])])
    }
  }

  const sortedRows = useMemo(() => {
    if (!data) return []
    return [...data.rows].sort((a, b) =>
      mode === 'volum' ? b.total_nok - a.total_nok : b.financed_count - a.financed_count
    )
  }, [data, mode])

  const pieData = useMemo(() => {
    if (!sortedRows.length) return []
    const top6 = sortedRows.slice(0, 6)
    const others = sortedRows.slice(6)
    const result = top6.map(r => ({
      name: r.bank_name,
      value: mode === 'volum' ? r.total_nok : r.financed_count,
    }))
    if (others.length > 0) {
      result.push({
        name: t('shared.others'),
        value: others.reduce(
          (s, r) => s + (mode === 'volum' ? r.total_nok : r.financed_count), 0
        ),
      })
    }
    return result
  }, [sortedRows, mode, t])

  const total = pieData.reduce((s, r) => s + r.value, 0)

  if (loading && !data) return <div className="text-gray-500">{t('bankTab.loading')}</div>
  if (error) return <div className="text-red-600">{t('shared.errorLabel')} {error}</div>

  return (
    <div className="space-y-6">
      <BruktbilfinansieringInfoBox
        bankName={bankName}
        filters={filters}
        tab="bankmarked"
        overrideClasses={selectedClasses}
      />

      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
          <button
            onClick={() => setMode('volum')}
            className={`px-4 py-1.5 transition-colors ${
              mode === 'volum'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {t('bankTab.byVolume')}
          </button>
          <button
            onClick={() => setMode('antall')}
            className={`px-4 py-1.5 transition-colors ${
              mode === 'antall'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {t('bankTab.byCount')}
          </button>
        </div>
      </div>

      {/* Class filter — grouped by seller type, same pattern as KlasserTab */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{t('shared.classesLabel')}</span>
          {Object.keys(SCOPE1_CLASS_GROUPS).map(group => (
            <button
              key={group}
              onClick={() => selectGroup(group)}
              className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            >
              {group}
            </button>
          ))}
          <button
            onClick={() => setSelectedClasses([...ALL_SCOPE1_CLASSES])}
            className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            {t('bankTab.selectAll')}
          </button>
          <button
            onClick={() => setSelectedClasses([])}
            className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            {t('shared.clearAll')}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {Object.entries(SCOPE1_CLASS_GROUPS).map(([group, classes]) => (
            <div key={group}>
              <button
                onClick={() => selectGroup(group)}
                className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 hover:underline"
              >
                {group}
              </button>
              {(classes as string[]).map(cls => (
                <label key={cls} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedClasses.includes(cls)}
                    onChange={() => toggleClass(cls)}
                    className="accent-primary-600"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {cls.replace(`${group}_TO_`, '')}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </div>

      {data && (
        <>
          {/* Summary totals */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-xs text-gray-500">{t('bankTab.totalLoans')}</div>
              <div className="text-2xl font-bold mt-1">{fmtNum(data.total_financed)}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-xs text-gray-500">{t('bankTab.totalVolume')}</div>
              <div className="text-2xl font-bold mt-1">{fmtNok(data.total_nok)}</div>
            </div>
          </div>

          {/* Pie chart + legend */}
          <div>
            <h2 className="text-lg font-semibold mb-3">{t('shared.marketShare')}</h2>
            <ResponsiveContainer width="100%" height={420}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={160}
                  innerRadius={60}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number, name: string) => [
                    mode === 'volum' ? fmtNok(v) : fmtNum(v),
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Custom legend: 2-column grid, sorted by value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4 text-sm">
              {pieData.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-xs shrink-0"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200">{entry.name}</span>
                  <span className="tabular-nums text-gray-500 dark:text-gray-400 shrink-0">
                    {total > 0 ? fmtPct(entry.value / total) : '–'}
                  </span>
                  <span className="tabular-nums text-gray-700 dark:text-gray-300 shrink-0 w-28 text-right">
                    {mode === 'volum' ? fmtNok(entry.value) : fmtNum(entry.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div>
            <h2 className="text-lg font-semibold mb-3">{t('shared.allBanks')}</h2>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-right w-10">#</th>
                  <th className="px-3 py-2 text-left">{t('shared.bank')}</th>
                  <th className="px-3 py-2 text-right">{t('shared.loansCount')}</th>
                  <th className="px-3 py-2 text-right">{t('shared.volume')}</th>
                  <th className="px-3 py-2 text-right">{t('shared.marketShare')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.length > 0 && (() => {
                  const totalCount = sortedRows.reduce((s, r) => s + (r.financed_count || 0), 0)
                  const totalNok = sortedRows.reduce((s, r) => s + (r.total_nok || 0), 0)
                  return (
                    <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                      <td className="px-3 py-2 text-right"></td>
                      <td className="px-3 py-2 font-bold text-primary-700 dark:text-primary-300">
                        <span className="text-primary-600 dark:text-primary-400 mr-1">∑</span>TOTAL
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNum(totalCount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNok(totalNok)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">100,0 %</td>
                    </tr>
                  )
                })()}
                {sortedRows.map((row, i) => (
                  <tr key={row.bank_org_nr} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2 text-right text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{row.bank_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(row.financed_count)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNok(row.total_nok)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPct(row.market_share_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
